import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Form, Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Table from '../../../../components/Table';
import {
    getAllVendorsAPI,
    getMasterEquipmentAPI,
    getStationNameAPI,
    registerDeviceMobius,
    startAndStopAlertAPI,
} from '../../../../API/RDPMS_API';
import moment from 'moment';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';
import { capitalizeFirstLetter } from '../../../../constants/Uppercase';
import { addEquipmentColumns, sizePerPageList } from '../../../../utils/ColumnUtils';
import { capitalize } from '../../../../utils/statusUtils';
import EnableDisableButtons from '../../../../components/Buttons/EnableDisableButtons';
import '../../../../../src/assets/scss/custom/components/_addEquipment.scss';
import * as bootstrap from 'bootstrap';
import AddMasterModal from '../../../../components/modal/AddMasterModal';
import EditMasterModal from '../../../../components/modal/EditMasterModal';
import DeleteMasterModal from '../../../../components/modal/DeleteMasterModal';

import { equipmentConfig } from '../../../masterConfig/equipmentConfig';

const EquipmentMaster = () => {
    const { t } = useTranslation();

    const [equipmentType, setEquipmentType] = useState('dccircuit');
    const config = equipmentConfig[equipmentType] || equipmentConfig.dccircuit;

    //  States
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [vendor, setVendor] = useState([]);
    const [stationName, setStationName] = useState([]);
    const [limit, setLimit] = useState(5);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showEditModalData, setShowEditModalData] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [equipmentToDelete, setEquipmentToDelete] = useState(null);
    const [selectedToDeleteDeviceId, setSelectedToDeleteDeviceId] = useState(null);

    //  Auth Info
    const { auth } = useSelector((state) => {
        const user = state.Auth.user?._j || state.Auth.user;
        return { auth: { ...state.Auth, user } };
    });

    const stationIds = auth?.user?.stationId || '';
    const userId = auth?.user?.id || '';
    const userRole = capitalizeFirstLetter(auth?.user?.role || 'Admin');

    //  Fetch all master data
    const fetchAllData = useCallback(async () => {
        try {
            setLoader(true);
            await Promise.all([getMasterEquipmentData(limit, pageNumber), getStationNameData(), getAllVendorsData()]);
        } catch (error) {
            console.error('Error fetching all data:', error);
        } finally {
            setLoader(false);
        }
    }, [limit, pageNumber, equipmentType]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    //  Vendor Data
    const getAllVendorsData = async () => {
        try {
            const response = await getAllVendorsAPI();
            if (Array.isArray(response) && response.length) setVendor(response);
        } catch (error) {
            console.error('Vendor fetch error:', error);
            toast.error(t('Failed to refresh vendor data'), { position: 'top-right' });
        }
    };

    //  Station Data
    const getStationNameData = async () => {
        try {
            const response = await getStationNameAPI({ userId });
            if (Array.isArray(response) && response.length) setStationName(response);
        } catch (error) {
            console.error('Station fetch error:', error);
        }
    };

    //  Master EQUIPMENT Data
    const getMasterEquipmentData = async (limits = limit, pages = pageNumber, showNoDataPopup = true) => {
        try {
            const request = { limit: limits, page: pages, stationId: stationIds };

            const response = await getMasterEquipmentAPI(request, equipmentType);
            // console.log(response, 'response........');

            if (response?.data?.length) {
                const formattedData = response.data.map((item) => ({
                    id: item.id,
                    createdDate: moment(item?.updatedAt).format('Do MMM YYYY, h:mm a'),
                    [config.uniqueFieldRequest]: item[config.uniqueFieldName],
                    assetTypeCode: item.asset_type_code,
                    assetNumberCode: item.asset_number_code,
                    smmsAssetCode: item.smms_asset_code,
                    smmsAssetName: item.smms_asset_name,
                    channel: item.channel,
                    device: item.device_id,
                    type: capitalize(item.type),
                    stationMasterName: capitalize(item?.stationDetails?.station_master),
                    stationName: item?.stationDetails?.name,
                    vendor: item.vendorDetails?.vendor_cloud_name || '',
                    city: item?.stationDetails?.city,
                    state: item?.stationDetails?.state,
                    parameterTypeId: item.parameter_type_id,
                    action: (
                        <div className="d-flex justify-content-center align-items-center gap-1">
                            <Link to="#" className="action-icon" onClick={() => openEditModalWithHeaderClass(item)}>
                                <i className="mdi mdi-pencil"></i>
                            </Link>
                            <Link
                                to="#"
                                className="action-icon text-danger"
                                onClick={() => {
                                    setSelectedToDeleteDeviceId(item.device_id);
                                    openDeleteModal(item.id);
                                }}>
                                <i className="mdi mdi-delete"></i>
                            </Link>
                        </div>
                    ),
                    stopAlert: (
                        <Form.Check
                            type="switch"
                            id={`custom-switch-${item.id}`}
                            checked={item.alert_status}
                            onChange={(e) => handleToggle(e, item.id)}
                        />
                    ),
                }));

                setData(formattedData);
                setPageNumber(response.currentPage || 1);
                setPageCount(response.totalPages || 1);
                setTotalCount(response.totalCount || 0);
                new bootstrap.Modal(document.getElementById('updateEquipment')).show();
            } else if (showNoDataPopup) {
                const modalInstance = new bootstrap.Modal(document.getElementById('alertNoDataModal'));
                modalInstance.show();
                setTimeout(() => modalInstance.hide(), 1000);
            }
        } catch (error) {
            console.error(`${config.name} fetch error:`, error);

            toast.error(t(`Failed to fetch ${config.name} data`), { position: 'top-right' });
        }
    };

    //  Alert Toggle (Single or All)
    const handleToggle = async (e, id, isAll = false) => {
        try {
            const request = {
                name: config.name,
                id,
                status: e.target.checked,
                isAll,
                stationId: stationIds,
            };

            const response = await startAndStopAlertAPI(request);

            if (response) getMasterEquipmentData(limit, pageNumber);
        } catch (error) {
            console.error(`${config.name} toggle alert error:`, error);
        }
    };

    const enableAllAlerts = () => handleToggle({ target: { checked: true } }, null, true);
    const disableAllAlerts = () => handleToggle({ target: { checked: false } }, null, true);

    //  Pagination
    const handlePageChange = (value) => {
        setLimit(value);
        setPageNumber(1);
    };
    const handlePageClick = (value) => setPageNumber(value);

    //  Modal Handlers
    const openModalWithHeaderClass = () => setShowModal(true);
    const closeModalWithHeaderClass = () => setShowModal(false);
    const openEditModalWithHeaderClass = (data) => {
        setShowEditModalData(data);
        setShowEditModal(true);
    };
    const closeEditModalWithHeaderClass = () => setShowEditModal(false);
    const openDeleteModal = (id) => {
        setEquipmentToDelete(id);
        setShowDeleteModal(true);
    };
    const closeDeleteModal = () => {
        setEquipmentToDelete(null);
        setShowDeleteModal(false);
    };

    //  Form Validation Schema
    const schemaResolver = yupResolver(
        yup.object({
            [config.uniqueFieldRequest]: yup
                .string()
                .required(t(`Please enter ${config.extraField.label.toLowerCase()}`)),

            assetTypeCode: yup.string().required(t('Please enter asset type code')),
            assetTypeId: yup.string().required(t('Please enter asset type id')),
            smmsAssetCode: yup.string().required(t('Please enter smms asset code')),
            smmsAssetName: yup.string().required(t('Please enter smms asset name')),
            assetNumberCode: yup.string().required(t('Please enter asset number code')),
            assetNumberId: yup.string().required(t('Please enter asset number id')),
            parameterTypeId: yup.string().required(t('Please enter parameter id')),
            vendorId: yup.string().required(t('Please select vendor')),
            deviceId: yup.string().required(t('Please enter device id')),
            channel: yup
                .number()
                .typeError(t('Please enter a valid number'))
                .required(t('Please enter channel number'))
                .transform((value, originalValue) => (originalValue === '' ? undefined : value)),
            type: yup.string().required(t('Please select type')),
            stationId: yup.string().required(t('Please select station name')),
        })
    );

    //  Submit (Add)
    const onSubmit = async (formData) => {
        try {
            setLoader(true);

            const payload = {
                'm2m:cin': {
                    con: {
                        ...formData,
                        type: capitalizeFirstLetter(formData.type),
                        createdBy: userRole,
                        updatedBy: userRole,
                        assetTypeName: config.assetTypeName,
                        status: 'active',
                    },
                },
            };

            // console.log(payload, 'payload........');
            const result = await registerDeviceMobius(payload);
            // console.log(result, 'result........');

            if (result?.isSucess === true) {
                setTimeout(() => {
                    getMasterEquipmentData(limit, pageNumber, false);
                }, 1000);

                setShowModal(false);

                toast.success(t(`Added ${config.name} successfully`), { position: 'top-right' });
            } else {
                toast.error(t(`Error adding ${config.name}`), { position: 'top-right' });
            }
        } catch (error) {
            console.error(`${config.name} add error:`, error);

            toast.error(t(`Error adding ${config.name}`), { position: 'top-right' });
        } finally {
            setLoader(false);
        }
    };

    //  Edit
    const onEdit = async (formData) => {
        try {
            setLoader(true);

            const payload = {
                'm2m:cin': {
                    con: {
                        ...formData,
                        id: showEditModalData.id,
                        type: capitalizeFirstLetter(formData.type),
                        updatedBy: userRole,
                        assetTypeName: config.assetTypeName,
                        status: 'active',
                    },
                },
            };

            const result = await registerDeviceMobius(payload);

            if (result?.isSucess === true) {
                setTimeout(() => {
                    getMasterEquipmentData(limit, pageNumber, false);
                }, 1000);

                setShowEditModal(false);

                toast.success(t(`Updated ${config.name} successfully`), { position: 'top-right' });
            } else {
                toast.error(t(`Error updating ${config.name}`), { position: 'top-right' });
            }
        } catch (error) {
            console.error(`${config.name} edit error:`, error);

            toast.error(t(`Error updating ${config.name}`), { position: 'top-right' });
        } finally {
            setLoader(false);
        }
    };

    //  Delete onDeleteEquipment
    const onDeleteEquipment = async () => {
        if (!equipmentToDelete) return;

        try {
            setLoader(true);

            const payload = {
                'm2m:cin': {
                    con: {
                        id: equipmentToDelete,
                        deviceId: selectedToDeleteDeviceId,
                        assetTypeName: config.assetTypeName,
                        status: 'inactive',
                    },
                },
            };

            const result = await registerDeviceMobius(payload);

            if (result?.isSucess === true) {
                setData((prev) => prev.filter((row) => row.id !== equipmentToDelete));
                setTotalCount((prev) => prev - 1);

                closeDeleteModal();
                await getMasterEquipmentData();

                toast.success(t(`Deleted ${config.name} successfully`), { position: 'top-right' });
            } else {
                toast.error(t(`Failed to delete ${config.name}`), { position: 'top-right' });
            }
        } catch (error) {
            console.error(`${config.name} delete error:`, error);

            toast.error(t(`Error deleting ${config.name}`), { position: 'top-right' });
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <Row>
                <Col>
                    <div className="page-title-box mb-4">
                        <div className="page-title-right">
                            <form className="d-flex">
                                <div className="input-group">
                                    <Dropdown className="me-0">
                                        <Dropdown.Toggle className="export text-black">
                                            {equipmentConfig[equipmentType].name}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="customDropdownMenu">
                                            {Object.keys(equipmentConfig).map((key) => (
                                                <Dropdown.Item
                                                    style={{
                                                        color: '#000000',
                                                    }}
                                                    key={key}
                                                    onClick={() => {
                                                        setData([]);
                                                        setEquipmentType(key);
                                                    }}>
                                                    {equipmentConfig[key].name}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    <Button className="export text-black" onClick={openModalWithHeaderClass}>
                                        {t(`ADD ${equipmentConfig[equipmentType].name.toUpperCase()}`)}
                                    </Button>

                                    <button
                                        className="update"
                                        id="update"
                                        type="button"
                                        onClick={() => getMasterEquipmentData(limit, pageNumber)}>
                                        {t('Update')} <i className="fas fa-sync-alt"></i>
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div style={{ marginLeft: '-2rem' }}>
                            <button className="btn_1">{t('Master Equipment')}</button>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            <Col className="d-flex justify-content-end align-items-cente">
                                <EnableDisableButtons
                                    onEnableAll={enableAllAlerts}
                                    onDisableAll={disableAllAlerts}
                                    t={t}
                                />
                            </Col>
                            <div
                                className="col-6 col-sm-6 card-add-equipment p-3 border-0 text-start no-radius my-2"
                                style={{
                                    width: '100%',
                                    boxShadow: '0px 0px 35px 0px rgba(154, 161, 171, 0.15)',
                                    position: 'relative',
                                }}>
                                <div className="equipment-table-wrapper">
                                    <Table
                                        columns={addEquipmentColumns(t, equipmentType, equipmentConfig)}
                                        data={data}
                                        pageSize={limit}
                                        pageCount={pageCount}
                                        pageNumber={pageNumber}
                                        totalCount={totalCount}
                                        currentPage={pageNumber}
                                        sizePerPageList={sizePerPageList}
                                        isSortable={true}
                                        pagination={true}
                                        isSearchable={true}
                                        onPageChange={handlePageChange}
                                        onPageClick={handlePageClick}
                                    />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <AddMasterModal
                t={t}
                title={`ADD ${config.name.toUpperCase()}`}
                showModal={showModal}
                closeModalWithHeaderClass={() => closeModalWithHeaderClass()}
                onSubmit={onSubmit}
                schemaResolver={schemaResolver}
                extraField={config.extraField}
                stationName={stationName}
                vendor={vendor}
                loader={loader}
            />

            <EditMasterModal
                t={t}
                title={`EDIT ${config.name.toUpperCase()}`}
                showModal={showEditModal}
                closeModalWithHeaderClass={() => closeEditModalWithHeaderClass()}
                onSubmit={onEdit}
                schemaResolver={schemaResolver}
                extraField={{
                    ...config.extraField,
                    value: showEditModalData?.[config.uniqueFieldName],
                }}
                showEditModalData={showEditModalData}
                stationName={stationName}
                vendor={vendor}
                loader={loader}
            />

            <DeleteMasterModal
                t={t}
                showModal={showDeleteModal}
                closeModal={() => closeDeleteModal()}
                onDelete={onDeleteEquipment}
                title={`Delete ${config.name}`}
                message={`Are you sure you want to delete this ${config.name}?`}
                imageSrc="/asset-images/deleteiconmodal.png"
            />

            <div
                className="modal fade"
                id="updateEquipment"
                tabIndex="-1"
                aria-labelledby="updateEquipmentLabel"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header close">
                            <button
                                type="button"
                                className="btn-close ms-auto"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body text-center">
                            <img
                                src="/asset-images/checkmark.png"
                                alt="Success"
                                className="img-fluid"
                                style={{ maxWidth: '100px' }}
                            />
                            <p className="mt-3"> {t(`${config.name} data refreshed successfully`)}</p>
                        </div>
                    </div>
                </div>
            </div>
            {/* No Data Modal */}
            <div
                className="modal fade"
                id="alertNoDataModal"
                tabIndex="-1"
                aria-labelledby="alertNoDataModalLabel"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header close">
                            <button
                                type="button"
                                className="btn-close ms-auto"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body text-center">
                            <img
                                src="/asset-images/checkmark.png"
                                alt="No Data"
                                className="img-fluid"
                                style={{ maxWidth: '100px' }}
                            />
                            <p className="mt-3"> {t(`No ${config.name} data available`)}</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EquipmentMaster;
